// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'
import { impresoraFragmentAst } from '../fragments/impresoraFragment.ts'
import { ImpresoraProps } from '../interfaces/impresora.ts'

const query = /* GraphQL */ gqlAst`
  ${impresoraFragmentAst}

  query IMPRESORA_LISTADO($codigoSucursal: Int!) {
    impresoraPorSucursal(codigoSucursal: $codigoSucursal) {
      ...impresoraFields
    }
  }
`

export const KEY_IMPRESORA_LISTADO = 'impresora-listado'

export interface ApiImpresoraListadoParams {
  codigoSucursal: number
}

/**
 * Listado de impresoras de una sucursal.
 * @param params Código de sucursal de la sesión.
 * @param options Headers adicionales y señal de cancelación de la petición.
 */
export const apiImpresoraListado = async (
  params: ApiImpresoraListadoParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ImpresoraProps[]> => {
  try {
    const data = await graphqlClient.request<{ impresoraPorSucursal: ImpresoraProps[] }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.impresoraPorSucursal
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida el listado de impresoras desde cualquier lugar (ej. onSuccess de mutaciones),
 * sin necesidad de tener montado el hook.
 * @example invalidateImpresoraListado()
 * @example invalidateImpresoraListado({ extraQueryKeys: [codigoSucursal] })
 * @author isi-template
 */
export const invalidateImpresoraListado = (options?: InvalidateQueryOptions) =>
  invalidateQuery(KEY_IMPRESORA_LISTADO, options)

/**
 * Hook para consultar el listado de impresoras de una sucursal.
 * queryKey: [KEY_IMPRESORA_LISTADO, codigoSucursal, ...(options.queryKeys ?? [])]
 * Para invalidar sin montar el hook, usar `invalidateImpresoraListado()`.
 * @param params Código de sucursal de la sesión.
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 * @author isi-template
 */
export const useImpresoraListado = (
  params: ApiImpresoraListadoParams,
  options: QueryOpts<ImpresoraProps[]> = {},
) =>
  useApiQuery(KEY_IMPRESORA_LISTADO, (ctx) => apiImpresoraListado(params, { signal: ctx.signal }), {
    refetchOnWindowFocus: false,
    ...options,
    queryKeys: [params.codigoSucursal, ...(options.queryKeys ?? [])],
  })
